// Which screen of a device takes touch, and what a visitor who touches one
// that does not is told. The room and the web player say the same words.

import type { DeviceEntry } from "./catalog.ts";
import { DEFAULT_LANG, translate, type Lang } from "./i18n.ts";

export type Screen = "primary" | "auxiliary";

/**
 * The screen of a device that takes touch: the lower one of a device with
 * two, the only one of a touch panel; null for a device whose screens take
 * none.
 */
export function touchScreen(device: Pick<DeviceEntry, "input" | "auxiliary">): Screen | null {
  return !device.input.touch ? null : device.auxiliary ? "auxiliary" : "primary";
}

/**
 * What a click or a tap on `screen` of `device` is answered with when that
 * screen takes no touch, or null when it takes touch.
 */
export function noTouchHint(device: Pick<DeviceEntry, "name" | "short" | "input" | "auxiliary">, screen: Screen, lang: Lang = DEFAULT_LANG): string | null {
  const touch = touchScreen(device);
  if (touch === screen) return null;
  const name = device.short ?? device.name;
  const which = translate(lang, device.auxiliary ? (screen === "primary" ? "touch.upper" : "touch.lower") : "touch.screen", { device: name });
  const instead = translate(lang, touch === "auxiliary" ? "touch.useLower" : touch === "primary" ? "touch.useUpper" : "touch.useKeys");
  return translate(lang, "touch.hint", { screen: which, instead });
}

/** How long a screen that takes no touch says nothing again after it said so. */
export const NO_TOUCH_QUIET_MS = 4000;

/**
 * A hint that is shown once and then not again until `quiet` milliseconds
 * after it was last shown: `due(now)` says whether to show it now, and
 * counts that it was.
 */
export function quietHint(quiet = NO_TOUCH_QUIET_MS): { due(now: number): boolean } {
  let last = -Infinity;
  return {
    due(now) {
      if (now - last < quiet) return false;
      last = now;
      return true;
    },
  };
}
